<template>
  <Scroller>
    <!-- Static sections -->
    <transition name="slide-fade" mode="out-in">
      <ScrollerSection id="intro" sticky="center" key="intro" v-if="!active">
        <div>
          <h1 class="f-title-5">How is the electricity mix changing?</h1>
          <ScrollIcon>Keep scrolling to read more</ScrollIcon>
        </div>
      </ScrollerSection>
      <ScrollerSection
        id="elecMixChangeShare"
        key="elecMixChangeShare"
        behind
        sticky="left"
        v-if="active === 'ElecMixChangeShare'"
      >
        <div class="electricity-trends chart__container">
          <div class="electricity-trend">
            <AreaColumn
              :scenario="'STEPS'"
              :max="100"
              units="%"
              showValues
              showValueUnit
              :chartData="data[active]"
            />
            <div style="width: 50px"></div>
            <AreaColumn
              :scenario="'APS'"
              :max="100"
              units="%"
              showValues
              showValueUnit
              :chartData="data[active]"
            />
          </div>
          <StaticLegendTest
            v-if="'ElecMixChangeShare' === active"
            spread
            :items="[
              {
                items: [
                  { label: 'Unabated fossil fuels', color: 'historic' },
                  { label: 'Renewables', color: 'blue' },
                  { label: 'Nuclear', color: 'red' },
                  { label: 'Other', color: 'darkgray' },
                ],
              },
            ]"
          />
        </div>
      </ScrollerSection>
      <ScrollerSection
        id="elecMixChangeAbs"
        key="elecMixChangeAbs"
        sticky="left"
        behind
        v-if="'ElecMixChangeAbs' === active"
      >
        <SquareCompares
          :data="data[active]"
          :max="ElecMixChangeAbsMax"
          :scenarios="['2010', '2021', 'STEPS 2030', 'APS 2030']"
        >
          <template #legend>
            <StaticLegendTest
              v-if="'ElecMixChangeAbs' === active"
              spread
              :items="[
                {
                  items: [
                    { label: 'Unabated fossil fuels', color: 'historic' },
                    { label: 'Renewables', color: 'blue' },
                    { label: 'Nuclear', color: 'red' },
                    { label: 'Other', color: 'darkgray' },
                  ],
                },
              ]"
            />
          </template>
        </SquareCompares>
      </ScrollerSection>
      <ScrollerSection
        id="elecDemandDrivers"
        key="elecDemandDrivers"
        sticky="left"
        behind
        v-if="'ElecDemandDrivers' === active"
      >
        <div class="chart__container">
          <span class="f-ui-2-bold">Electric cars (Million units)</span>
          <div style="margin-bottom: 10px; border-bottom: 1px solid #000">
            <div
              style="display: flex; align-items: center; margin-bottom: 10px"
            >
              <div
                class="background-gray"
                style="width: 2px; height: 2px"
              ></div>
              <div class="color-gray" style="margin-left: 10px">
                ← 20 000 in 2010
              </div>
            </div>
            <BoxCompare
              v-for="item in [
                { count: 17, bgColor: 'historic' },
                { count: 167, bgColor: 'STEPS' },
                { count: 214, bgColor: 'APS' },
              ]"
              :key="item.bgColor"
              v-bind="item"
              ><span
                class="box-compare-label f-ui-2-bold"
                :class="[`color-${item.bgColor}`]"
              >
                + {{ item.count }}</span
              >
            </BoxCompare>
          </div>
          <div>
            <BarChart
              v-for="item in formatDemandDriverData(data[active])"
              :key="item.group"
              :height="30"
              :max="ElecDemandDriversMax"
              :customItemMax="
                Math.max(
                  ...itemsForMax
                    .find((d) => d.group === item.group)
                    .children.map((d) => d.value)
                )
              "
              :items="item.children"
            >
              <span class="f-ui-2-bold">{{ item.group }}</span>
              <template #double>
                <div style="display: flex; flex-flow: column">
                  <div
                    v-for="scenario in data[active].find(
                      (d) => d.group === item.group
                    ).misc"
                    :key="`scenario-${scenario.name}`"
                    :class="{
                      [`background-${scenario.color}`]: scenario.color,
                    }"
                    :style="{
                      height: '15px',
                      width: `${getWidth(scenario, item, data[active])}%`,
                    }"
                  ></div>
                </div>
              </template>
            </BarChart>
            <div class="bar-chart-x-axis">
              <div
                v-for="tick in [0, '3 000 TWh']"
                :key="tick"
                style="margin-top: 5px"
              >
                {{ tick }}
              </div>
            </div>
          </div>
          <StaticLegendTest
            v-if="'ElecDemandDrivers' === active"
            spread
            :items="[
              {
                items: [
                  { label: '2010', color: 'gray' },
                  { label: 'Change 2010-21', color: 'historic' },
                  { label: 'Change STEPS 2021-30', color: 'STEPS' },
                  { label: 'Change APS 2021-30', color: 'APS' },
                ],
              },
            ]"
          />
        </div>
      </ScrollerSection>
      <ScrollerSection
        id="powerCapacity"
        key="powerCapacity"
        sticky="left"
        behind
        v-if="'PowerCapacity' === active"
      >
        <div class="chart__container">
          <BarChart
            v-for="(item, idx) in data[active]"
            :key="item.group"
            :height="20"
            :max="100"
            :items="item.children"
          >
            <span class="f-ui-2-bold">{{ item.group }}</span>
            <template #misc>
              <div
                style="
                  position: absolute;
                  height: 100%;
                  border-left: 2px solid #ffc000;
                "
                :style="{ left: `${APSWidth[idx].width}%` }"
              />
            </template>
          </BarChart>
          <div class="bar-chart-x-axis">
            <div
              v-for="tick in [0, '100%']"
              :key="tick"
              style="margin-top: 5px"
            >
              {{ tick }}
            </div>
          </div>
          <StaticLegendTest
            v-if="'PowerCapacity' === active"
            spread
            :items="[
              {
                items: [
                  { label: 'Solar PV', color: 'red' },
                  { label: 'Wind', color: 'purple' },
                  { label: 'Bioenergy', color: 'teal' },
                  { label: 'Hydro', color: 'blue' },
                  { label: 'Other renewables', color: 'darkgray' },
                  {
                    label: 'APS 2050',
                    color: 'white',
                    lineColor: 'APS',
                    type: 'line',
                  },
                ],
              },
            ]"
          />
        </div>
      </ScrollerSection>
    </transition>
    <!-- Normal sections -->
    <ScrollerSection
      id="ElecMixChangeShare"
      @enter="handleEnter"
      @leave="handleLeave"
    >
      <Block>
        <p>
          Low-emissions sources of electricity, led by renewables, are poised to
          overtake fossil fuels by 2030 in the STEPS and APS, ending decades of
          growth for coal.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="ElecMixChangeAbs" @enter="handleEnter">
      <Block>
        <p>
          Here is what the breakdown of each source
          <strong>of electricity</strong> by scenario looks like.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="ElecDemandDrivers" @enter="handleEnter">
      <Block>
        <h4 class="f-title-5">What drives electricity demand growth?</h4>
        <p>
          Global electricity demand rises by 25-30% to 2030 in the STEPS and APS
          due to more electric motors, EVs, heat pumps and hydrogen.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="PowerCapacity" @enter="handleEnter">
      <Block>
        <h4 class="f-title-5">What new power capacity will be built?</h4>
        <p>
          Renewables are set to dominate global capacity additions, accounting
          for 75-80% of all new capacity to 2050 in the STEPS and APS, led by
          solar PV and wind.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="fin" @enter="handleEnter" />
  </Scroller>
</template>

<script>
import {
  Scroller,
  ScrollerSection,
  ScrollIcon,
  Block,
} from "@international-energy-agency/vue-ui";
import ActiveMixin from "../mixins/active";
import LeftBlock from "../components/LeftBlock.vue";
import BarChart from "../components/BarChart.vue";
import AreaColumn from "../components/AreaColumn.vue";
import SquareCompares from "../components/SquareCompares.vue";
import StaticLegendTest from "../components/StaticLegendTest.vue";
import BoxCompare from "../components/BoxCompare.vue";

export default {
  components: {
    Scroller,
    StaticLegendTest,
    ScrollerSection,
    ScrollIcon,
    Block,
    BarChart,
    BoxCompare,
    // LeftBlock,
    AreaColumn,
    SquareCompares,
  },
  mixins: [ActiveMixin],
  data() {
    return {
      ElecMixChangeAbsMax: 17570,
      ElecDemandDriversMax: 3000,
      data: {
        ElecMixChangeAbs: [
          {
            source: "How to read",
            label: true,
            type: "instruction",
            data: [8670, 10200, 9040, 8080],
          },
          {
            source: "Coal unabated",
            type: "unabated",
            data: [8670, 10200, 9040, 8080],
          },
          {
            source: "Natural gas unabated",
            type: "unabated",
            data: [4860, 6550, 6850, 6100],
          },
          {
            source: "Other",
            type: "other",
            data: [1020, 740, 520, 580],
          },
          {
            source: "Nuclear",
            type: "nuclear",
            data: [2760, 2780, 3350, 3550],
          },
          {
            source: "Renewables",
            type: "renewables",
            data: [4230, 8060, 15070, 17570],
          },
          {
            source: "Wind",
            type: "renewables",
            data: [340, 1870, 4600, 5820],
          },
          {
            source: "Solar PV",
            type: "renewables",
            data: [30, 1000, 4010, 4840],
          },
          {
            source: "Other renewables",
            type: "renewables",
            data: [3860, 5190, 6460, 6920],
          },
        ],
        ElecMixChangeShare: {
          STEPS: [
            {
              name: "Unabated fossil fuels",
              data: [
                [2010, 67],
                [2021, 62],
                [2030, 47],
              ],
              className: "color-historic",
            },
            {
              name: "Renewables",
              data: [
                [2010, 20],
                [2021, 28],
                [2030, 43],
              ],
              className: "color-blue",
            },
            {
              name: "Nuclear",
              data: [
                [2010, 13],
                [2021, 10],
                [2030, 10],
              ],
              className: "color-red",
            },
          ],
          APS: [
            {
              name: "Unabated fossil fuels",
              data: [
                [2021, 62],
                [2030, 40],
              ],
              className: "color-historic",
            },
            {
              name: "Renewables",
              data: [
                [2021, 28],
                [2030, 49],
              ],
              className: "color-blue",
            },
            {
              name: "Nuclear",
              data: [
                [2021, 10],
                [2030, 10],
              ],
              className: "color-red",
            },
            {
              name: "Other",
              data: [
                [2021, 0],
                [2030, 1],
              ],
              className: "color-darkgray",
            },
          ],
        },
        ElecDemandDrivers: [
          {
            group: "Hydrogen",
            children: [
              { name: "2010", value: 0, color: "gray" },
              { name: "2021", value: 0, color: "historic" },
            ],
            misc: [
              { name: "STEPS 2030", value: 159, color: "STEPS" },
              { name: "APS 2030", value: 879, color: "APS" },
            ],
          },
          {
            group: "Aluminium",
            children: [
              { name: "2010", value: 860, color: "gray" },
              { name: "2021", value: 1392, color: "historic" },
            ],
            misc: [
              { name: "STEPS 2030", value: 1449, color: "STEPS" },
              { name: "APS 2030", value: 1449, color: "APS" },
            ],
          },
          {
            group: "Chemicals",
            children: [
              { name: "2010", value: 1033, color: "gray" },
              { name: "2021", value: 1430, color: "historic" },
            ],
            misc: [
              { name: "STEPS 2030", value: 1628, color: "STEPS" },
              { name: "APS 2030", value: 1750, color: "APS" },
            ],
          },
          {
            group: "Space heating",
            children: [
              { name: "2010", value: 1348, color: "gray" },
              { name: "2021", value: 1651, color: "historic" },
            ],
            misc: [
              { name: "STEPS 2030", value: 2098, color: "STEPS" },
              { name: "APS 2030", value: 1973, color: "APS" },
            ],
          },
          {
            group: "Space cooling",
            children: [
              { name: "2010", value: 1410, color: "gray" },
              { name: "2021", value: 1998, color: "historic" },
            ],
            misc: [
              { name: "STEPS 2030", value: 2791, color: "STEPS" },
              { name: "APS 2030", value: 2599, color: "APS" },
            ],
          },
        ],
        PowerCapacity: [
          {
            group: "China",
            children: [
              { name: "Solar PV", value: 53.71, color: "red" },
              { name: "Wind", value: 26.85, color: "purple" },
              { name: "Bioenergy", value: 1.12, color: "teal" },
              { name: "Hydro", value: 3.16, color: "blue" },
              { name: "Other renewables", value: 0.33, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.8278, color: "APS" },
          },
          {
            group: "Central and South America",
            children: [
              { name: "Solar PV", value: 45.41, color: "red" },
              { name: "Wind", value: 19.66, color: "purple" },
              { name: "Bioenergy", value: 3.59, color: "teal" },
              { name: "Hydro", value: 13.99, color: "blue" },
              { name: "Other renewables", value: 0.89, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.8519, color: "APS" },
          },
          {
            group: "European Union",
            children: [
              { name: "Solar PV", value: 38.21, color: "red" },
              { name: "Wind", value: 33.36, color: "purple" },
              { name: "Bioenergy", value: 2.36, color: "teal" },
              { name: "Hydro", value: 4.9, color: "blue" },
              { name: "Other renewables", value: 2.48, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.785, color: "APS" },
          },
          {
            group: "Japan",
            children: [
              { name: "Solar PV", value: 53.46, color: "red" },
              { name: "Wind", value: 17.81, color: "purple" },
              { name: "Bioenergy", value: 2.78, color: "teal" },
              { name: "Hydro", value: 5.08, color: "blue" },
              { name: "Other renewables", value: 1.9, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.8514, color: "APS" },
          },
          {
            group: "India",
            children: [
              { name: "Solar PV", value: 53.59, color: "red" },
              { name: "Wind", value: 18.53, color: "purple" },
              { name: "Bioenergy", value: 0.77, color: "teal" },
              { name: "Hydro", value: 3.5, color: "blue" },
              { name: "Other renewables", value: 0.41, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.7861, color: "APS" },
          },
          {
            group: "World",
            children: [
              { name: "Solar PV", value: 45.58, color: "red" },
              { name: "Wind", value: 21.96, color: "purple" },
              { name: "Bioenergy", value: 1.52, color: "teal" },
              { name: "Hydro", value: 5.15, color: "blue" },
              { name: "Other renewables", value: 1.1, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.7878, color: "APS" },
          },
          {
            group: "Korea",
            children: [
              { name: "Solar PV", value: 46.63, color: "red" },
              { name: "Wind", value: 23.85, color: "purple" },
              { name: "Bioenergy", value: 0.64, color: "teal" },
              { name: "Hydro", value: 0.46, color: "blue" },
              { name: "Other renewables", value: 0.76, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.7512, color: "APS" },
          },
          {
            group: "United States",
            children: [
              { name: "Solar PV", value: 48.09, color: "red" },
              { name: "Wind", value: 19.4, color: "purple" },
              { name: "Bioenergy", value: 0.86, color: "teal" },
              { name: "Hydro", value: 2.07, color: "blue" },
              { name: "Other renewables", value: 0.65, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.7668, color: "APS" },
          },
          {
            group: "Africa",
            children: [
              { name: "Solar PV", value: 32.11, color: "red" },
              { name: "Wind", value: 15.16, color: "purple" },
              { name: "Bioenergy", value: 1.55, color: "teal" },
              { name: "Hydro", value: 14.14, color: "blue" },
              { name: "Other renewables", value: 4.51, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.7939, color: "APS" },
          },
          {
            group: "Southeast Asia",
            children: [
              { name: "Solar PV", value: 38.06, color: "red" },
              { name: "Wind", value: 10.49, color: "purple" },
              { name: "Bioenergy", value: 2.56, color: "teal" },
              { name: "Hydro", value: 11.65, color: "blue" },
              { name: "Other renewables", value: 2.31, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.8585, color: "APS" },
          },
          {
            group: "Middle East",
            children: [
              { name: "Solar PV", value: 35.31, color: "red" },
              { name: "Wind", value: 11.69, color: "purple" },
              { name: "Bioenergy", value: 1.19, color: "teal" },
              { name: "Hydro", value: 1.84, color: "blue" },
              { name: "Other renewables", value: 3.87, color: "darkgray" },
            ],
            APS: { name: "APS 2050", value: 0.7249, color: "APS" },
          },
        ],
      },
    };
  },
  methods: {
    formatDemandDriverData(data) {
      return [...data].map((item) => ({
        ...item,
        children: [
          ...item.children,
          {
            ...item.misc.find(
              (val) => val.value === Math.max(...item.misc.map((d) => d.value))
            ),
            color: "#fff",
          },
        ].map((child, idx, arr) => ({
          ...child,
          value:
            idx === 1
              ? child.value - arr[0].value
              : idx >= 2
              ? child.value - arr[1].value
              : child.value,
        })),
      }));
    },
    getWidth(scenario, item, data) {
      let misc = data.find((d) => d.group === item.group).misc;
      let children = data.find((d) => d.group === item.group).children;
      let maxValueFromChildren = children[children.length - 1].value;
      let value = scenario.value - maxValueFromChildren;
      let max = Math.max(...misc.map((d) => d.value - maxValueFromChildren));
      return (value / max) * 100;
    },
  },
  computed: {
    APSWidth() {
      return this.data.PowerCapacity.map((item) => {
        let max = item.children.reduce((a, b) => a + b.value, 0);
        return { width: (item.APS.value * 10000) / max };
      });
    },
    itemsForMax() {
      let result = [];
      let data = this.data.ElecDemandDrivers;
      data.forEach((item) => {
        result.push({
          ...item,
          children: [...item.children, ...item.misc],
        });
      });
      return result;
    },
  },
};
</script>

<style scoped>
@import url("../css/main.css");
@import url("../css/colors.css");
.bar-chart-x-axis {
  border-top: 1px solid #000;
  width: 100%;
  display: flex;
  justify-content: space-between;
}
.electricity-trends {
  width: 100%;
  display: flex;
  flex-flow: column;
}
.electricity-trend {
  display: flex;
}
.electricity-bars {
  padding-top: 80px;
  flex: 0 1 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  height: 100%;
}
.box-compare-label {
  display: flex;
  align-items: center;
  margin-left: 10px;
}
.left-tick {
  border-left: 1px solid #000;
}
.right-tick {
  border-right: 1px solid #000;
}
@media screen and (max-width: 729px) {
  .bar-chart-x-axis {
    font-size: 10px;
    max-height: 12px;
  }
}
</style>
